import type { RouteObject } from 'react-router-dom';
import { RequirePagePermissions } from '@/entities/user';
import type { RamRouteObject } from '@/shared/lib/permissions';

/**
 * Walk a RAM route tree and wrap every `element` whose route has
 * `permissions` set in a `<RequirePagePermissions>` gate.
 *
 * Routes without `permissions` are passed through unchanged. The
 * returned tree is a plain react-router `RouteObject[]` ready to be
 * handed to `createBrowserRouter`.
 *
 * Only `element` is wrapped — `lazy`, `Component`, `loader`, etc. stay
 * untouched so the existing router shape keeps working.
 */
export function applyPermissionGuards(routes: RamRouteObject[]): RouteObject[] {
    return routes.map(transformRoute);
}

function transformRoute(route: RamRouteObject): RouteObject {
    const { permissions, children, element, ...rest } = route;

    const wrappedChildren = children ? children.map(transformRoute) : undefined;

    const wrappedElement =
        permissions && permissions.length > 0 && element != null ? (
            <RequirePagePermissions permissions={permissions}>{element}</RequirePagePermissions>
        ) : (
            element
        );

    return {
        ...rest,
        ...(wrappedElement !== undefined ? { element: wrappedElement } : {}),
        ...(wrappedChildren ? { children: wrappedChildren } : {}),
    } as RouteObject;
}
